"use client";
import { useState, useEffect } from "react";
import Link from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/navigation';

const Page = () => {
  const router = useRouter();

  // States for dynamic count
  const [gnomeCount, setGnomeCount] = useState<number | null>(null);
  const [proteinCount, setProteinCount] = useState<number | null>(null);
  const [antigenCount, setAntigenCount] = useState<number | null>(null);
  const [epitopeCount, setEpitopeCount] = useState<number | null>(null);
  const [epitopePredictedCount, setEpitopePredictedCount] = useState<number | null>(null);
  const [epitopeValidatedCount, setEpitopeValidatedCount] = useState<number | null>(null);

  // States for search
  const [antigenSearchTerm, setAntigenSearchTerm] = useState("");
  const [epitopeSearchTerm, setEpitopeSearchTerm] = useState("");

  const fetchCount = async () => {
    try {
      const response = await fetch("/api/count-gnome-epitope-antigen", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setGnomeCount(data.gnome);
        setProteinCount(data.protein);
        setAntigenCount(data.antigen);
        setEpitopeCount(data.epitope);
        setEpitopePredictedCount(data.epitope_predicted);
        setEpitopeValidatedCount(data.epitope_validated);
      } else {
        console.error("Failed to fetch counts");
      }
    } catch (error) {
      console.error("Error fetching count:", error);
    }
  };

  useEffect(() => {
    fetchCount();
  }, []);

  // Search functions
  const handleAntigenSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (antigenSearchTerm.trim()) {
      router.push(`/antigen?search=${encodeURIComponent(antigenSearchTerm.trim())}`);
    } else {
      router.push('/antigen');
    }
  };

  const handleEpitopeSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (epitopeSearchTerm.trim()) {
      router.push(`/epitope/all?search=${encodeURIComponent(epitopeSearchTerm.trim())}`);
    } else {
      router.push('/epitope/all');
    }
  };

  return (
    <>
      <Head>
        <title>Home - ASFV-EpiBase</title>
        <meta name="description" content="ASFV-EpiBase - Home page with genome, antigen, and epitope data overview for African Swine Fever Virus research" />
      </Head>
      <div className="flex flex-col items-center p-6">

      {/* Dynamic Count Section */}
      <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 text-center py-6">
        <div>
          <div className="text-orange-600 font-bold text-md">Genomes</div>
          <div className="text-4xl font-semibold text-gray-800">
            <Link href={'/asfv-genome'}>{gnomeCount !== null ? gnomeCount : '...'}</Link>
          </div>
        </div>
        <div>
          <div className="text-orange-600 font-bold text-md">Proteins</div>
          <div className="text-4xl font-semibold text-gray-800">
            <Link href={'/protein'}>{proteinCount !== null ? proteinCount : '...'}</Link>
          </div>
        </div>
        <div>
          <div className="text-orange-600 font-bold text-md">Antigens</div>
          <div className="text-4xl font-semibold text-gray-800">
             <Link href={'/antigen'}>{antigenCount !== null ? antigenCount : '...'}</Link>
          </div>
        </div>
        <div>
          <div className="text-orange-600 font-bold text-md">Predicted Epitopes</div>
          <div className="text-4xl font-semibold text-gray-800">
            <Link href={'/epitope/all?has_references=false'}>{epitopePredictedCount !== null ? epitopePredictedCount : '...'}</Link> 
          </div>
        </div>
        <div>
          <div className="text-orange-600 font-bold text-md">Experimental Verified Epitopes</div>
          <div className="text-4xl font-semibold text-gray-800">
            <Link href={'/epitope/all?has_references=true'}>{epitopeValidatedCount !== null ? epitopeValidatedCount : '...'}</Link> 
          </div>
        </div>
      </div>

      {/* Search Sections */}
      <div className="w-full max-w-6xl space-y-8 mb-8">
        
        {/* Find Antigens Section */}
        <div className="bg-white p-6 rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Find Antigens</h2>
          <p className="text-gray-600 mb-4">Search for specific antigens in the ASFV-EpiBase database</p>
          <form onSubmit={handleAntigenSearch} className="flex gap-4">
            <input
              type="text"
              value={antigenSearchTerm}
              onChange={(e) => setAntigenSearchTerm(e.target.value)}
              placeholder="Enter antigen or protein name"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-medium"
            >
              Search Antigens
            </button>
          </form>
          <div className="mt-2">
            <Link href="/antigen" className="text-orange-600 hover:text-orange-700 text-sm">
              Browse all antigens →
            </Link>
          </div>
        </div>

        {/* Find Epitopes Section */}
        <div className="bg-white p-6 rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Find Epitopes</h2>
          <p className="text-gray-600 mb-4">Search for specific epitopes in the ASFV-EpiBase database</p>
          <form onSubmit={handleEpitopeSearch} className="flex gap-4">
            <input
              type="text"
              value={epitopeSearchTerm}
              onChange={(e) => setEpitopeSearchTerm(e.target.value)}
              placeholder="Enter epitope or peptide sequence"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-medium"
            >
              Search Epitopes
            </button>
          </form>
          <div className="mt-2">
            <Link href="/epitope/all" className="text-orange-600 hover:text-orange-700 text-sm">
              Browse all epitopes →
            </Link>
          </div>
        </div>
      </div>

      {/* ASFV-EpiBase Description */}
      
      <div className="flex w-full max-w-6xl flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-6 p-6 border rounded-lg shadow-lg bg-white">
        <div className="text-gray-700 leading-relaxed space-y-4">
          <p className="text-justify">
            <strong className="text-orange-600">ASFV-EpiBase</strong> is a specialized database dedicated to antigens and epitopes of <em>African swine fever virus</em> (ASFV). ASFV is a highly contagious viral disease that threatens global pig production, causing severe economic losses and representing a major challenge for animal health and food security. Despite decades of research, no effective commercial vaccine is currently available, making the discovery of novel epitopes and vaccine candidates a critical priority.
          </p>
          
          <p className="text-justify">
            <strong className="text-orange-600">ASFV-EpiBase</strong> has been developed to address this urgent need by providing a centralized, curated, and comprehensive resource for ASFV immunological data. It integrates information from experimentally validated studies as well as computational predictions, enabling researchers to:
          </p>
          
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li><strong>Explore antigens and epitopes</strong> specific to ASFV across multiple strains and isolates.</li>
            <li><strong>Access detailed annotations</strong> on immune response, host species, antigen localization, and experimental evidence.</li>
            <li><strong>Compare epitope profiles</strong> to support the design of multi-epitope vaccines, diagnostics, and immunotherapeutics.</li>
            <li><strong>Bridge genomics and immunoinformatics</strong>, offering insights into ASFV antigenicity and immune evasion mechanisms</li>
          </ul>
        </div>
      </div>

      {/* How to Cite Section */}
      <div className="w-full max-w-6xl mt-8">
        <div className="bg-white p-6 rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Cite</h2>
          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-orange-500">
            <p className="text-gray-700 italic">
              Paopit Siriarchawatana, Supawadee Ingsriswang and Anan Jongkaewwattana (2025). ASFV-EpiBase: A curated database of African swine fever virus antigens and epitopes.
            </p>
            <p className="text-gray-600 mt-2">
              Retrieved from <a href="https://epibase.tbrcnetwork.org/" className="text-orange-600 hover:text-orange-700 underline">https://epibase.tbrcnetwork.org/</a>
            </p>
          </div>
        </div>
      </div>

      {/* Information Resources Section */}
      <div className="w-full max-w-6xl mt-8">
        <div className="bg-white p-6 rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Information Resources</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Learn about the computational methods and prediction tools used in ASFV-EpiBase for identifying T-cell epitopes (cytotoxic and helper) and B-cell epitopes. Our database integrates multiple established algorithms including NetMHCpan, NetMHCIIpan, TCRLens, BepiPred 3.0, and other prediction methods.
          </p>
          <Link href="/instructions" className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-medium">
            More info →
          </Link>
        </div>
      </div>
      </div>
    </>
  );
};

export default Page;